// SYNC-GUARD: byte-identical twin of the other device-fingerprint.ts
// (Web/src/app/shared/ and WebApp/PlayoffPredictor.Web.Net10/src/shared/).
// Any change MUST be applied verbatim to both copies.

export interface DeviceSignals {
  userAgent: string; language: string; platform: string;
  hardwareConcurrency: number; deviceMemory: number;
  screenW: number; screenH: number; colorDepth: number;
  timeZone: string; canvasHash: string; webglRenderer: string;
}

export function buildSignalString(s: DeviceSignals): string {
  return [
    s.userAgent, s.language, s.platform, s.hardwareConcurrency, s.deviceMemory,
    s.screenW, s.screenH, s.colorDepth, s.timeZone, s.canvasHash, s.webglRenderer
  ].join('||');
}

export async function sha256Hex(input: string): Promise<string> {
  if (typeof crypto === 'undefined' || !crypto.subtle)
  {
    return '';
  }
  const data = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(digest))
    .map(b => b.toString(16).padStart(2, '0')).join('');
}

function canvasHash(): string {
  try {
    const c = document.createElement('canvas');
    const ctx = c.getContext('2d');
    if (!ctx)
    {
      return '';
    }
    ctx.textBaseline = 'top'; ctx.font = '14px Arial';
    ctx.fillText('pp-fp', 2, 2);
    return c.toDataURL().slice(-32);
  } catch { return ''; }
}

function webglRenderer(): string {
  try {
    const gl = document.createElement('canvas').getContext('webgl') as WebGLRenderingContext | null;
    if (!gl)
    {
      return '';
    }
    const dbg = gl.getExtension('WEBGL_debug_renderer_info');
    return dbg ? String(gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL)) : '';
  } catch { return ''; }
}

let _promise: Promise<string> | null = null;
export function computeDeviceFingerprint(): Promise<string> {
  if (!_promise)
  {
    _promise = _compute();
  }
  return _promise;
}

async function _compute(): Promise<string> {
  if (typeof window === 'undefined' || typeof navigator === 'undefined' || typeof screen === 'undefined')
  {
    return '';
  }
  const nav = navigator as Navigator & { deviceMemory?: number };
  const sig: DeviceSignals = {
    userAgent: nav.userAgent ?? '',
    language: nav.language ?? '',
    platform: nav.platform ?? '',
    hardwareConcurrency: nav.hardwareConcurrency ?? 0,
    deviceMemory: nav.deviceMemory ?? 0,
    screenW: screen.width, screenH: screen.height, colorDepth: screen.colorDepth,
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone ?? '',
    canvasHash: canvasHash(), webglRenderer: webglRenderer()
  };
  return sha256Hex(buildSignalString(sig));
}
